import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { BLOG_ARTICLES, type BlogArticle, type BlogContentSection } from '../data/blogArticles';
export interface CmsBlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  status: string;
  date: string;
  views: number;
  content: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  imageUrl: string;
  tags: string[];
  featured?: boolean;
}
const STORAGE_KEY = 'astriqo_cms_posts';
const DEFAULT_CMS_POSTS: CmsBlogPost[] = [{
  id: 1,
  title: 'Ретроградный Меркурий 2026: полный гид',
  excerpt: 'Всё, что нужно знать о ретроградном Меркурии в этом году.',
  category: 'Астрология',
  status: 'published',
  date: '14 Мар 2026',
  views: 1240,
  content: '## Что меняется глобально\n\nРетроградный Меркурий в 2026 году пройдёт через три знака: Овен, Лев и Стрелец.\n\n## Какие профессии станут востребованными\n\nВ период ретрограда особенно ценятся аналитики и редакторы.\n\n## Как адаптироваться\n\nГлавное правило — не начинать ничего нового, а завершать начатое.',
  seoTitle: 'Ретроградный Меркурий 2026',
  seoDescription: 'Узнайте всё о ретроградном Меркурии в 2026 году.',
  seoKeywords: 'ретроградный меркурий, 2026',
  imageUrl: '',
  tags: ['ретроград', 'Меркурий', 'прогноз 2026']
}, {
  id: 2,
  title: 'Как читать натальную карту: пошаговая инструкция',
  excerpt: 'Подробный гайд для начинающих по чтению натальной карты.',
  category: 'Обучение',
  status: 'published',
  date: '12 Мар 2026',
  views: 890,
  content: '## Что такое натальная карта\n\nНатальная карта — это снимок неба в момент вашего рождения.\n\n## Основные элементы\n\nПланеты, знаки, дома и аспекты.\n\n## Как начать чтение\n\nНачните с Солнца, Луны и Асцендента.',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  imageUrl: '',
  tags: ['обучение', 'натальная карта']
}, {
  id: 3,
  title: 'Лунный календарь на апрель 2026',
  excerpt: 'Благоприятные и неблагоприятные дни по лунному календарю.',
  category: 'Календарь',
  status: 'published',
  date: '10 Мар 2026',
  views: 2100,
  content: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  imageUrl: '',
  tags: ['лунный календарь']
}, {
  id: 4,
  title: 'Совместимость знаков: мифы и реальность',
  excerpt: 'Разбираем популярные заблуждения о совместимости знаков зодиака.',
  category: 'Астрология',
  status: 'draft',
  date: '08 Мар 2026',
  views: 0,
  content: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  imageUrl: '',
  tags: ['совместимость']
}, {
  id: 5,
  title: 'Транзиты Плутона в 2026 году',
  excerpt: 'Как транзиты Плутона повлияют на каждый знак зодиака.',
  category: 'Прогнозы',
  status: 'published',
  date: '05 Мар 2026',
  views: 560,
  content: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  imageUrl: '',
  tags: ['Плутон', 'транзиты']
}, {
  id: 6,
  title: 'Что такое соляр и зачем он нужен',
  excerpt: 'Объясняем простым языком, что такое солярная карта.',
  category: 'Обучение',
  status: 'draft',
  date: '01 Мар 2026',
  views: 0,
  content: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  imageUrl: '',
  tags: ['соляр', 'обучение']
}];
function loadFromStorage(): CmsBlogPost[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return DEFAULT_CMS_POSTS;
}
function saveToStorage(posts: CmsBlogPost[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
  } catch {}
}
function parseMdContent(md: string): {
  lead: string;
  sections: BlogContentSection[];
} {
  if (!md || !md.trim()) return {
    lead: '',
    sections: []
  };
  const lines = md.split('\n');
  let lead = '';
  const sections: BlogContentSection[] = [];
  let currentSection: BlogContentSection | null = null;
  let currentParagraph = '';
  const flushParagraph = () => {
    if (currentParagraph.trim()) {
      if (currentSection) {
        currentSection.paragraphs.push(currentParagraph.trim());
      } else {
        lead += (lead ? ' ' : '') + currentParagraph.trim();
      }
    }
    currentParagraph = '';
  };
  for (const line of lines) {
    if (line.startsWith('## ') || line.startsWith('### ')) {
      flushParagraph();
      const heading = line.replace(/^#{2,3}\s+/, '');
      const id = heading.toLowerCase().replace(/[^a-zа-яё0-9]+/gi, '-').replace(/^-|-$/g, '');
      currentSection = {
        id,
        heading,
        paragraphs: []
      };
      sections.push(currentSection);
    } else if (line.startsWith('> ')) {
      flushParagraph();
      if (currentSection) {
        currentSection.quote = line.replace(/^>\s*/, '');
      }
    } else if (line.startsWith('- ')) {
      flushParagraph();
      if (currentSection) {
        if (!currentSection.list) currentSection.list = [];
        currentSection.list.push(line.replace(/^-\s*/, ''));
      }
    } else if (line.match(/^\d+\.\s/)) {
      flushParagraph();
      if (currentSection) {
        if (!currentSection.list) currentSection.list = [];
        currentSection.list.push(line.replace(/^\d+\.\s*/, ''));
      }
    } else if (line.trim() === '') {
      flushParagraph();
    } else {
      currentParagraph += (currentParagraph ? ' ' : '') + line;
    }
  }
  flushParagraph();
  return {
    lead,
    sections
  };
}
function cmsPostToArticle(post: CmsBlogPost): BlogArticle {
  const parsed = parseMdContent(post.content);
  const slug = `cms-${post.id}`;
  return {
    id: slug,
    slug,
    title: post.title,
    excerpt: post.excerpt,
    category: post.category,
    date: new Date().toISOString().split('T')[0],
    readTime: `${Math.max(1, Math.ceil(post.content.length / 1000))} мин`,
    author: 'Администратор',
    authorInitials: 'АД',
    coverColor: 'bg-indigo-100',
    coverImage: post.imageUrl || undefined,
    content: {
      lead: parsed.lead || post.excerpt,
      sections: parsed.sections
    },
    seo: post.seoTitle ? {
      ru: {
        title: post.seoTitle,
        description: post.seoDescription,
        keywords: post.seoKeywords
      }
    } : undefined,
    tags: post.tags || [],
    featured: post.featured || false
  };
}
interface BlogContextValue {
  cmsPosts: CmsBlogPost[];
  setCmsPosts: React.Dispatch<React.SetStateAction<CmsBlogPost[]>>;
  allArticles: BlogArticle[];
  findArticle: (idOrSlug: string) => BlogArticle | undefined;
}
const BlogContext = createContext<BlogContextValue | null>(null);
export function BlogProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cmsPosts, setCmsPostsRaw] = useState<CmsBlogPost[]>(loadFromStorage);
  const setCmsPosts: React.Dispatch<React.SetStateAction<CmsBlogPost[]>> = useCallback(action => {
    setCmsPostsRaw(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      saveToStorage(next);
      return next;
    });
  }, []);
  const allArticles = useMemo(() => {
    const cmsArticles = cmsPosts.filter(p => p.status === 'published').map(cmsPostToArticle);
    return [...cmsArticles, ...BLOG_ARTICLES];
  }, [cmsPosts]);
  const findArticle = (idOrSlug: string): BlogArticle | undefined => {
    return allArticles.find(a => a.id === idOrSlug || a.slug === idOrSlug);
  };
  return <BlogContext.Provider value={{
    cmsPosts,
    setCmsPosts,
    allArticles,
    findArticle
  }} data-id="element-20745">
      {children}
    </BlogContext.Provider>;
}
export function useBlog() {
  const ctx = useContext(BlogContext);
  if (!ctx) throw new Error('useBlog must be used within BlogProvider');
  return ctx;
}